/**
 * When does a guest's demo end, and why (AD-794 §5.3, §6.2, §6.4)? Pure, so each trigger is a
 * unit test rather than a timing accident in the game view.
 *
 *   - `completed`: the final quest (`GET /api/guest/config` `finalQuestId`, design §5.2's
 *     `echo-face-the-sentinel`) reaches `turned_in` — "Turn in to Sel → the end card".
 *   - `expired`: the session's fixed life ran out (the card says "Time's up —").
 *   - `lost`: the connection failed before the life ran out (the instance is gone with it: "demo
 *     sessions aren't saved" — a reload is a NEW guest, §6.2).
 *   - `left`: the player chose "End demo".
 */

import type { GuestEndReason } from './guestFlow.js';

/** A small hold after the final turn-in, so its completion card (QuestCompleteCard) is seen before the end card. */
export const COMPLETION_CARD_DELAY_MS = 2_500;

/** Slack on the expiry comparison: a socket closed this close to the end IS the end. */
export const EXPIRY_SLACK_MS = 5_000;

export interface QuestStatusView {
  questId: string;
  status: string;
}

/** True once the configured final quest has been turned in. A null id never completes. */
export function finalQuestTurnedIn(
  quests: readonly QuestStatusView[] | undefined,
  finalQuestId: string | null,
): boolean {
  if (finalQuestId === null || quests === undefined) return false;
  return quests.some((q) => q.questId === finalQuestId && q.status === 'turned_in');
}

/** Milliseconds until the session's fixed expiry (never negative; NaN-safe ⇒ 0). */
export function msUntilExpiry(expiresAtIso: string, nowMs: number): number {
  const at = Date.parse(expiresAtIso);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, at - nowMs);
}

/**
 * The end reason for a connection that FAILED (the socket gave up: kicked at teardown, resume
 * window lapsed, auth refused because the session expired). Near or past the expiry it is the
 * TTL; earlier it is a lost connection.
 */
export function endReasonForFailure(expiresAtIso: string, nowMs: number): GuestEndReason {
  return msUntilExpiry(expiresAtIso, nowMs) <= EXPIRY_SLACK_MS ? 'expired' : 'lost';
}
